import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup } from "@angular/forms";

import { DIALOG_DATA, DialogRef, DialogService } from "@bitwarden/components";

import { SharedModule } from "../shared";

export type AddCustomDomainDialogData = {
  domains: string[];
};

export type AddCustomDomainDialogResult = string[];

@Component({
  templateUrl: "add-custom-domain-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule],
})
export class AddCustomDomainDialogComponent {
  readonly formGroup: FormGroup<{ domains: FormArray<FormControl<string>> }>;

  constructor(
    @Inject(DIALOG_DATA) data: AddCustomDomainDialogData,
    private readonly dialogRef: DialogRef<AddCustomDomainDialogResult>,
    private readonly formBuilder: FormBuilder,
  ) {
    const initialDomains = data.domains.length > 0 ? data.domains : [""];
    this.formGroup = this.formBuilder.group({
      domains: this.formBuilder.array(
        initialDomains.map((domain) => this.createDomainControl(domain)),
      ),
    });
  }

  get domainControls(): FormControl<string>[] {
    return this.formGroup.controls.domains.controls;
  }

  addDomainRow(): void {
    this.formGroup.controls.domains.push(this.createDomainControl(""));
  }

  removeDomainRow(index: number): void {
    const domains = this.formGroup.controls.domains;
    domains.removeAt(index);
    if (domains.length === 0) {
      domains.push(this.createDomainControl(""));
    }
  }

  readonly submit = async () => {
    const domains = this.formGroup.controls.domains.value
      .map((domain) => domain.trim())
      .filter((domain) => domain !== "");

    void this.dialogRef.close(domains);
  };

  static open(dialogService: DialogService, data: AddCustomDomainDialogData) {
    return dialogService.open<AddCustomDomainDialogResult>(AddCustomDomainDialogComponent, {
      data,
    });
  }

  private createDomainControl(value: string): FormControl<string> {
    return this.formBuilder.control(value, { nonNullable: true });
  }
}
